import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LogOut, Menu, ChevronDown, Moon, Sun, Settings, TrendingUp, Briefcase } from 'lucide-react';
import { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function Navigation({ user, onLogout, currentPage, isAuthLoading = false }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const navVersion = "v5"; // Force navigation update - fixed flash issue
  
  // Check if user is an advisor - use cached role during loading to prevent flash
  const cachedRole = localStorage.getItem('userRole');
  const isAdvisor = user?.role === 'advisor' || (isAuthLoading && cachedRole === 'advisor');
  
  // Show loading skeleton only when we have no cached role to prevent any menu flash
  if (isAuthLoading && !cachedRole) {
    return (
    <nav className="glass mb-8 rounded-2xl p-4 dark:bg-gray-800 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
            <span className="text-white font-bold text-lg">FA</span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">FinAI</h2>
            <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
        </div>
        <div className="hidden lg:flex items-center space-x-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    </nav>
    );
  }
  
  // Handle clean navigation (clears all query params)
  const handleCleanNavigation = (path) => {
    navigate(path, { replace: false });
  };

  // Advisor-only navigation (clean, focused)
  const advisorNavigationStructure = [
    { name: 'Dashboard', path: '/advisor/dashboard', type: 'single' },
    { 
      name: 'Workspace', 
      type: 'dropdown',
      icon: Briefcase,
      items: [
        { name: 'Client Dashboard', path: '/advisor/clients' },
        { name: 'My Plans', path: '/advisor/plans' },
        { name: 'Create Plan', path: '/advisor/create-plan' },
      ]
    },
    { name: 'Plans & Billing', path: '/advisor/pricing', type: 'single', forceReload: true },
    { 
      name: 'Tools', 
      type: 'dropdown',
      items: [
        { name: 'Financial Calculators', path: '/calculators' },
        { name: 'Retirement Calculator', path: '/retirement-calculator' },
      ]
    },
    { name: 'Ask FinAI', path: '/advisor/ai-chat', type: 'single', featured: true },
  ];

  // Regular B2C navigation - Clean 5-category structure
  const consumerNavigationStructure = [
    { name: 'Dashboard', path: '/dashboard', type: 'single' },
    { 
      name: 'Discover', 
      type: 'dropdown',
      icon: TrendingUp,
      items: [
        { name: 'Market Overview', path: '/discover-investments' },
        { name: 'Stock Screener', path: '/stock-screener' },
        { name: 'Market News', path: '/market-news' },
        { name: 'Quality Picks', path: '/quality-picks' },
        { name: 'Learn', path: '/learn' },
      ]
    },
    { 
      name: 'My Finances', 
      type: 'dropdown',
      items: [
        { name: 'Transactions', path: '/transactions' },
        { name: 'Budgets', path: '/budgets' },
        { name: 'Bills', path: '/bills' },
        { name: 'Goals', path: '/goals' },
        { name: 'Investments', path: '/investments' },
        { name: 'Debts & Loans', path: '/loans' },
        { name: 'Net Worth', path: '/net-worth' },
        { name: 'Reports', path: '/reports' },
        { name: 'Smart Alerts', path: '/alerts' },
      ]
    },
    { 
      name: 'Tools', 
      type: 'dropdown',
      items: [
        { name: 'Financial Calculators', path: '/calculators' },
        { name: 'Loan Calculators', path: '/loan-calculators' },
        { name: 'Retirement Calculator', path: '/retirement-calculator' },
      ]
    },
    { 
      name: 'Ask FinAI', 
      type: 'dropdown',
      featured: true,
      items: [
        { name: 'Chat with FinAI', path: '/ai-advisor' },
        { name: 'Portfolio Analyzer', path: '/portfolio-analyzer' },
        { name: 'Spending Insights', path: '/spending-insights' },
        { name: 'Financial Plan', path: '/financial-planner' },
      ]
    },
  ];
  
  // Use appropriate navigation based on role
  const navigationStructure = isAdvisor ? advisorNavigationStructure : consumerNavigationStructure;

  const isActiveInDropdown = (items) => {
    return items.some(item => item.path === currentPage);
  };

  return (
    <nav data-testid="main-navigation" className="mb-8 rounded-2xl p-4 bg-white/70 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm shadow-black/[0.03] dark:shadow-black/20 relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-teal-500/40 to-transparent" />
      <div className="flex items-center justify-between relative">
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-md shadow-teal-500/25">
            <span className="text-white font-bold text-lg">FA</span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">FinAI</h2>
            {user && <p className="text-xs text-gray-500 dark:text-gray-400">{user.name}</p>}
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-1">
          {navigationStructure.map((item, index) => (
            item.type === 'single' ? (
              item.forceReload ? (
                <a key={item.path} href={item.path}>
                  <Button
                    variant={currentPage === item.path ? 'default' : 'ghost'}
                    size="sm"
                    className={`whitespace-nowrap ${
                      item.featured 
                        ? currentPage === item.path 
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700' 
                          : 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-600 hover:to-pink-700'
                        : currentPage === item.path 
                          ? 'bg-teal-500 text-white' 
                          : 'text-gray-700 dark:text-gray-200 hover:bg-teal-50 dark:hover:bg-gray-700'
                    }`}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                  >
                    {item.icon && <item.icon className="w-4 h-4 mr-1.5" />}
                    {item.name}
                  </Button>
                </a>
              ) : (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={currentPage === item.path ? 'default' : 'ghost'}
                    size="sm"
                    className={`whitespace-nowrap ${
                      item.featured 
                        ? currentPage === item.path 
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700' 
                          : 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-600 hover:to-pink-700'
                        : currentPage === item.path 
                          ? 'bg-teal-500 text-white' 
                          : 'text-gray-700 dark:text-gray-200 hover:bg-teal-50 dark:hover:bg-gray-700'
                    }`}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                  >
                    {item.icon && <item.icon className="w-4 h-4 mr-1.5" />}
                    {item.name}
                  </Button>
                </Link>
              )
            ) : (
              <DropdownMenu key={index}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={isActiveInDropdown(item.items) ? 'default' : 'ghost'}
                    size="sm"
                    className={`whitespace-nowrap ${
                      item.featured 
                        ? isActiveInDropdown(item.items)
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700'
                          : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600'
                        : isActiveInDropdown(item.items) 
                          ? 'bg-teal-500 text-white' 
                          : 'text-gray-700 dark:text-gray-200 hover:bg-teal-50 dark:hover:bg-gray-700'
                    }`}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                  >
                    {item.icon && <item.icon className="w-4 h-4 mr-1.5" />}
                    {item.name}
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 dark:bg-gray-800">
                  {item.items.map((subItem) => (
                    <DropdownMenuItem 
                      key={subItem.path}
                      onClick={() => handleCleanNavigation(subItem.path)}
                      className={`cursor-pointer dark:text-gray-200 dark:hover:bg-gray-700 ${currentPage === subItem.path ? 'bg-teal-50 text-teal-700 dark:bg-teal-900 dark:text-teal-300' : ''}`}
                      data-testid={`nav-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {subItem.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )
          ))}
          <Button
            onClick={toggleTheme}
            variant="ghost"
            size="sm"
            className="whitespace-nowrap ml-2 dark:text-gray-300"
            data-testid="theme-toggle"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Link to="/account-settings">
            <Button
              variant="ghost"
              size="sm"
              className="whitespace-nowrap ml-2 dark:text-gray-300"
              data-testid="account-settings-button"
              title="Account Settings"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </Link>
          <Button
            onClick={onLogout}
            variant="ghost"
            size="sm"
            className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 whitespace-nowrap"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="lg:hidden">
          <Button
            variant="ghost"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden mt-4 space-y-1 pb-4" data-testid="mobile-menu">
          {navigationStructure.map((item, index) => (
            item.type === 'single' ? (
              item.forceReload ? (
                <a key={item.path} href={item.path} onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    variant={currentPage === item.path ? 'default' : 'ghost'}
                    className={`w-full justify-start font-medium ${
                      item.featured 
                        ? currentPage === item.path 
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700' 
                          : 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-600 hover:to-pink-700'
                        : currentPage === item.path 
                          ? 'bg-teal-500 text-white' 
                          : 'text-gray-700 dark:text-gray-200 hover:bg-teal-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {item.icon && <item.icon className="w-4 h-4 mr-2" />}
                    {item.name}
                  </Button>
                </a>
              ) : (
                <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    variant={currentPage === item.path ? 'default' : 'ghost'}
                    className={`w-full justify-start font-medium ${
                      item.featured 
                        ? currentPage === item.path 
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700' 
                          : 'bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:from-purple-600 hover:to-pink-700'
                        : currentPage === item.path 
                          ? 'bg-teal-500 text-white' 
                          : 'text-gray-700 dark:text-gray-200 hover:bg-teal-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {item.icon && <item.icon className="w-4 h-4 mr-2" />}
                    {item.name}
                  </Button>
                </Link>
              )
            ) : (
              <div key={index} className="pt-3 pb-1">
                <div className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded ${
                  item.featured 
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
                    : 'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800'
                }`}>
                  {item.name}
                </div>
                <div className="ml-2 mt-1 space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
                  {item.items.map((subItem) => (
                    <Button
                      key={subItem.path}
                      onClick={() => {
                        handleCleanNavigation(subItem.path);
                        setMobileMenuOpen(false);
                      }}
                      variant={currentPage === subItem.path ? 'default' : 'ghost'}
                      size="sm"
                      className={`w-full justify-start text-sm ${currentPage === subItem.path ? 'bg-teal-500 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-teal-50 dark:hover:bg-gray-700'}`}
                    >
                      {subItem.name}
                    </Button>
                  ))}
                </div>
              </div>
            )
          ))}
          <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-3 space-y-1">
            <Button
              onClick={toggleTheme}
              variant="ghost"
              className="w-full justify-start dark:text-gray-200"
              data-testid="mobile-theme-toggle"
            >
              {theme === 'dark' ? (
                <>
                  <Sun className="w-4 h-4 mr-2" />
                  Light Mode
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4 mr-2" />
                  Dark Mode
                </>
              )}
            </Button>
            <Link to="/account-settings" className="w-full" onClick={() => setMobileMenuOpen(false)}>
              <Button
                variant="ghost"
                className="w-full justify-start dark:text-gray-200"
              >
                <Settings className="w-4 h-4 mr-2" />
                Account Settings
              </Button>
            </Link>
            <Button
              onClick={onLogout}
              variant="ghost"
              className="w-full justify-start text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}